import {
	booleanAttribute,
	ChangeDetectionStrategy,
	Component,
	computed,
	effect,
	inject,
	input,
	signal,
} from '@angular/core';
import { RootReducer, Store } from '@app/app.reducers';
import { Dialog, open } from '@app/dialog/reducers/dialog.reducer';
import { FALLBACK_PRODUCT_IMAGE_SRC } from '@app/shared/common';
import { ProductImageService, ProofImageDetails } from '@app/shared/services/product-image.service';
import { type RemoteData, Initialized } from '@granodigital/grano-remote-data';

/**
 * Component for displaying product images with zoom functionality.
 *
 * For editable cart items with proof images, pass proofImageDetails
 * to enable automatic regeneration on image load errors.
 *
 * For static product images or order products, proofImageDetails can be omitted.
 */
@Component({
	standalone: true,
	selector: 'g-product-image',
	templateUrl: './product-image.component.html',
	styleUrls: ['./product-image.component.scss'],
	imports: [],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductImageComponent {
	private readonly store = inject(Store<RootReducer.State>);
	private readonly productImageService = inject(ProductImageService);

	readonly images = input.required<api.ProductImageDto[] | undefined>();
	readonly key = input<keyof api.ProductImageDto>('fitIn_80x80');
	readonly imageIndex = input(0);
	readonly proofImageDetails = input<ProofImageDetails | undefined>(undefined);

	readonly isZoomable$ = input(true, { alias: 'isZoomable', transform: booleanAttribute });
	readonly altText$ = input('a product', { alias: 'altText' });
	readonly localizedAltText$ = computed(() => {
		const imageOfText = $localize`:@@ProductImageAltText:Image of`;
		return `${imageOfText} ${this.altText$()}`;
	});

	/** Get the input image URL from component inputs (may be undefined) */
	private readonly inputImageUrl$ = computed(() => this.images()?.[this.imageIndex()]?.[this.key()]);

	// Signal to track image error recovery state
	private readonly imageState = signal<RemoteData<Error, string>>(new Initialized());

	constructor() {
		// Set initial state when URL changes since the browser will immediately start loading
		effect(() =>
			this.imageState.set(this.productImageService.getInitialImageState(this.inputImageUrl$())),
		);
	}

	/** Get the final image URL to display (includes fallback logic) */
	readonly currentImageUrl$ = computed(() =>
		this.productImageService.getDisplayUrl(
			this.inputImageUrl$(),
			this.imageState(),
			FALLBACK_PRODUCT_IMAGE_SRC,
		),
	);

	/** Check if the product has images available for zooming */
	readonly hasZoomableImages$ = computed(() => (this.images()?.length ?? 0) > 0);

	/** Handle zoom click - opens image viewer modal */
	handleZoom(): void {
		if (!this.isZoomable$() || !this.hasZoomableImages$()) return;

		// TODO (ED-10): Use DialogService directly.
		this.store.dispatch(
			open(
				Dialog.IMAGE_VIEWER,
				{ images: this.images(), key: 'fitIn_2000x2000', imageIndex: this.imageIndex() },
				{ scrollable: false, windowClass: 'modal-transparent' },
			),
		);
	}

	/** Handle image load error - try regeneration for proof images, show fallback for static images or failed regeneration */
	handleImageError(): void {
		this.productImageService
			.handleImageError(this.inputImageUrl$() ?? '', this.proofImageDetails(), this.imageState())
			.subscribe((newState) => this.imageState.set(newState));
	}

	/** Handle successful image load */
	handleImageLoad(): void {
		this.imageState.set(
			this.productImageService.handleImageLoad(
				this.inputImageUrl$(),
				this.currentImageUrl$(),
				this.imageState(),
			),
		);
	}
}
